import { LengthAwarePage, OutboundShipment, OutboundShipmentProductLine, OutboundShipmentProductLineMovement, OutboundShipmentProductLineServiceLine, Shipment } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import { moneyInterface, PaginatedResults, requestFilterInterface } from '@/compiler/types';
import baseAxios from '@/helpers/forms/axios';

export interface UpdateShipmentRequestData {
    name?: string
    notes?: string
    internalNotes?: string
}

export type UpdateShipmentFn = (shipmentId: number, data: UpdateShipmentRequestData) => Promise<Shipment>;

interface OutboundShipmentsApiReturn {
    getExpectedChargesTotal: (shipmentId: number) => Promise<moneyInterface>
    setShipFromAddress: (shipmentId: number, addressId: number) => Promise<void>
    loadArchivedShipments: (page: number) => Promise<PaginatedResults<OutboundShipment>>
    updateIsArchived: (shipmentId: number, isArchived: boolean) => Promise<void>
    ship: (shipmentId: number) => Promise<void>
    delete: (shipmentId: number) => Promise<void>
    requestCancellation: (shipmentId: number, reason?: string) => Promise<void>
    withdrawCancellationRequest: (shipmentId: number) => Promise<void>
    denyCancellationRequest: (shipmentId: number, reason?: string) => Promise<void>
    updateIsCaseForwarding: (shipmentId: number, isCaseForwarding: boolean) => Promise<void>
    updateIsLocked: (shipmentId: number, isLocked: boolean) => Promise<void>
    close: (shipmentId: number) => Promise<void>
    markReadyToShip: (shipmentId: number) => Promise<void>
    isOutboundShipment: (shipment: Shipment) => shipment is OutboundShipment
    downloadPickList: (shipmentIds: number[]) => Promise<string>
    fetchOutboundShipments: (page: number, filters?: requestFilterInterface[]) => Promise<PaginatedResults<OutboundShipment>>
    fetchOutboundShipment: (shipmentId: number) => Promise<OutboundShipment>
    createOutboundShipment: (name: string, warehouseId: number, notes?: string) => Promise<{ shipmentId: number }>
    updateOutboundShipment: (shipmentId: number, data: UpdateShipmentRequestData) => Promise<OutboundShipment>
    removeTagFromOutboundShipment: (shipmentId: number, tagId: number) => Promise<void>
    addTagToOutboundShipment: (shipmentId: number, tagId: number) => Promise<void>
    manageOutboundShipmentServices: (shipmentId: number, services: Array<{ id: number; quantity: number }>) => Promise<void>
    productLines: {
        getPage: (shipmentId: number, page?: number, perPage?: number) => Promise<LengthAwarePage<OutboundShipmentProductLine>>
        get: (shipmentId: number, productLineUuid: string) => Promise<OutboundShipmentProductLine>
        create: (shipmentId: number, form: {
            productUuid: string
            expiryDate: string|null
        }) => Promise<OutboundShipmentProductLine>
        update: (shipmentId: number, productLineUuid: string, form: {
            expiryDate: string|null
        }) => Promise<OutboundShipmentProductLine>
        delete: (shipmentId: number, productLineUuid: string) => Promise<void>
        movements: {
            getPage: (shipmentId: number, productLineUuid: string) => Promise<LengthAwarePage<OutboundShipmentProductLineMovement>>
            get: (shipmentId: number, productLineUuid: string, movementUuid: string) => Promise<OutboundShipmentProductLineMovement>
            create: (
                shipmentId: number,
                productLineUuid: string,
                form: {
                    productTraceabilityGroupUuid: string|null
                    productPackagingUuid: string|null
                    quantity: number
                    sourceLocationUuid: string
                    note: string|null
                }) => Promise<OutboundShipmentProductLineMovement>
            update: (
                shipmentId: number,
                productLineUuid: string,
                movementUuid: string,
                form: {
                    productTraceabilityGroupUuid: string|null
                    productPackagingUuid: string|null
                    quantity: number
                    sourceLocationUuid: string
                    note: string|null
                }) => Promise<OutboundShipmentProductLineMovement>
            delete: (shipmentId: number, productLineUuid: string, movementUuid: string) => Promise<void>
        }
        serviceLines: {
            getPage: (shipmentId: number, productLineUuid: string) => Promise<LengthAwarePage<OutboundShipmentProductLineServiceLine>>
            get: (shipmentId: number, productLineUuid: string, serviceLineUuid: string) => Promise<OutboundShipmentProductLineServiceLine>
            create: (shipmentId: number, productLineUuid: string, form: {
                serviceUuid: string
            }) => Promise<OutboundShipmentProductLineServiceLine>
            delete: (shipmentId: number, productLineUuid: string, serviceLineUuid: string) => Promise<void>
        }
    }
}

export default (axios: AxiosInstance = baseAxios): OutboundShipmentsApiReturn => ({
    getExpectedChargesTotal: async (shipmentId: number): Promise<moneyInterface> => {
        const response = await axios.get<{ expectedChargeTotal: moneyInterface }>(`/api/shipments/outbound/${shipmentId}/expected-charges-total`);

        return response.data.expectedChargeTotal;
    },

    setShipFromAddress: async (shipmentId: number, addressId: number): Promise<void> => {
        return await axios.post(`/api/shipments/outbound/${shipmentId}/set-ship-from-address`, {
            shipFromAddressId: addressId,
        });
    },

    loadArchivedShipments: async (page: number): Promise<PaginatedResults<OutboundShipment>> => {
        const response = await axios.get<PaginatedResults<OutboundShipment>>(`/api/shipments/outbound/archived?page=${page}`);

        return response.data;
    },

    updateIsArchived: async (shipmentId: number, isArchived: boolean): Promise<void> => {
        return await axios.put(`/api/shipments/outbound/${shipmentId}/archive`, { isArchived });
    },

    ship: async (shipmentId: number): Promise<void> => {
        return await axios.post(`/api/shipments/outbound/${shipmentId}/ship`);
    },

    delete: async (shipmentId: number): Promise<void> => {
        return await axios.delete(`/api/shipments/outbound/${shipmentId}`);
    },

    requestCancellation: async (shipmentId: number, reason?: string): Promise<void> => {
        return await axios.post(`/api/shipments/outbound/${shipmentId}/cancellation-request`, { reason });
    },

    withdrawCancellationRequest: async (shipmentId: number): Promise<void> => {
        return await axios.delete(`/api/shipments/outbound/${shipmentId}/cancellation-request`);
    },

    denyCancellationRequest: async (shipmentId: number, reason?: string): Promise<void> => {
        return await axios.post(`/api/shipments/outbound/${shipmentId}/cancellation-request/deny`, { reason });
    },

    updateIsCaseForwarding: async (shipmentId: number, isCaseForwarding: boolean): Promise<void> => {
        return await axios.put(`/api/shipments/outbound/${shipmentId}/case-forwarding`, { isCaseForwarding });
    },

    updateIsLocked: async (shipmentId: number, isLocked: boolean): Promise<void> => {
        return await axios.put(`/api/shipments/outbound/${shipmentId}/lock`, { isLocked });
    },

    close: async (shipmentId: number): Promise<void> => {
        return await axios.post(`/api/shipments/outbound/${shipmentId}/close`);
    },

    markReadyToShip: async (shipmentId: number): Promise<void> => {
        return await axios.post(`/api/shipments/outbound/${shipmentId}/ready-to-ship`);
    },

    isOutboundShipment: (shipment: Shipment): shipment is OutboundShipment => {
        return 'isCaseForwarding' in shipment;
    },

    downloadPickList: async (shipmentIds: number[]): Promise<string> => {
        const url = new URL('/api/shipments/outbound/pick-list', location.origin);
        shipmentIds.forEach(id => url.searchParams.append('ids[]', id.toString()));
        const response = await baseAxios.get<{ url: string }>(url.toString());
        return response.data.url;
    },

    fetchOutboundShipments: async (page: number, filters?: requestFilterInterface[]): Promise<PaginatedResults<OutboundShipment>> => {
        const url = new URL('/api/shipments/outbound', location.origin);
        url.searchParams.append('page', page.toString());
        if (filters) {
            filters.forEach((filter) => {
                url.searchParams.append(`filters[${filter.name}]`, filter.value);
            });
        }

        const response = await axios.get<PaginatedResults<OutboundShipment>>(url.toString());

        return response.data;
    },

    fetchOutboundShipment: async (shipmentId: number): Promise<OutboundShipment> => {
        const response = await axios.get<{ shipment: OutboundShipment }>(`/api/shipments/outbound/${shipmentId}`);

        return response.data.shipment;
    },

    createOutboundShipment: async (name: string, warehouseId: number, notes?: string): Promise<{ shipmentId: number }> => {
        const response = await axios.post<{ shipmentId: number }>('/api/shipments/outbound', {
            name,
            warehouseId,
            notes,
        });

        return response.data;
    },

    updateOutboundShipment: async (shipmentId: number, data: UpdateShipmentRequestData): Promise<OutboundShipment> => {
        const response = await axios.put<{ shipment: OutboundShipment }>(`/api/shipments/outbound/${shipmentId}`, data);

        return response.data.shipment;
    },

    removeTagFromOutboundShipment: async (shipmentId: number, tagId: number): Promise<void> => {
        return await axios.delete(`/api/shipments/outbound/${shipmentId}/tags/${tagId}`);
    },

    addTagToOutboundShipment: async (shipmentId: number, tagId: number): Promise<void> => {
        return await axios.post(`/api/shipments/outbound/${shipmentId}/tags/${tagId}`);
    },

    manageOutboundShipmentServices: async (shipmentId: number, services: Array<{ id: number; quantity: number }>): Promise<void> => {
        await axios.post(`/api/shipments/outbound/${shipmentId}/services`, { services });
    },

    productLines: {
        getPage: (shipmentId, page = 1, perPage = 100) => axios.get(`/api/shipments/outbound/${shipmentId}/product-lines`, { params: { page, per_page: perPage } }).then(response => response.data),
        get: (shipmentId, productLineUuid) => axios.get(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}`).then(response => response.data.data),
        create: (shipmentId, form) => axios.post(`/api/shipments/outbound/${shipmentId}/product-lines`, form).then(response => response.data.data),
        update: (shipmentId, productLineUuid, form) => axios.put(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}`, form).then(response => response.data.data),
        delete: (shipmentId, productLineUuid) => axios.delete(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}`),
        movements: {
            getPage: (shipmentId, productLineUuid) => axios.get(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}/movements`).then(response => response.data),
            get: (shipmentId, productLineUuid, movementUuid) => axios.get(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}/movements/${movementUuid}`).then(response => response.data.data),
            create: (shipmentId, productLineUuid, form) => axios.post(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}/movements`, form).then(response => response.data.data),
            update: (shipmentId, productLineUuid, movementUuid, form) => axios.put(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}/movements/${movementUuid}`, form).then(response => response.data.data),
            delete: (shipmentId, productLineUuid, movementUuid) => axios.delete(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}/movements/${movementUuid}`),
        },
        serviceLines: {
            getPage: (shipmentId, productLineUuid) => axios.get(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}/service-lines`).then(response => response.data),
            get: (shipmentId, productLineUuid, serviceLineUuid) => axios.get(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}/service-lines/${serviceLineUuid}`).then(response => response.data.data),
            create: (shipmentId, productLineUuid, form) => axios.post(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}/service-lines`, form).then(response => response.data.data),
            delete: (shipmentId, productLineUuid, serviceLineUuid) => axios.delete(`/api/shipments/outbound/${shipmentId}/product-lines/${productLineUuid}/service-lines/${serviceLineUuid}`),
        },
    },
});
